import { useLayoutEffect, useRef, useState } from 'react';

/** The control at rest: the button system's `m` icon step. */
export const CONTROL_SIZE = '2.75rem';

/** Its corner, from the same step — a rounded square, not a disc. */
export const CONTROL_RADIUS = '1rem';

/**
 * The glyph's inset at that size. Twice this plus a 1rem glyph is exactly
 * `CONTROL_SIZE`, which is what lets the open width be the shut width plus the
 * label and nothing else.
 */
export const GLYPH_INSET = '0.875rem';

/** Opening a label is a sweep, not a spring — see TileShareMenu. */
export const SWEEP_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

/** The label's own insets: the leading edge, and the gap to the glyph. */
export const LABEL_CLASS =
    'block pr-[0.75rem] pl-[0.875rem] text-[0.875rem] font-medium whitespace-nowrap';

/**
 * The width a control needs with its name out, measured rather than guessed.
 *
 * Every attempt to stand this up as a constant drifted: the caps were tuned
 * against an assumed glyph size, then against a stale build, and each time one
 * control ended up with slack in front of its label while another clipped the
 * gap behind it. The label is the only thing that varies, so measure it and add
 * it to the shut size — the glyph and its two insets already come to exactly
 * that.
 *
 * Returns the ref to put on the label and the width to open to.
 */
export const useLabelledWidth = (label: string) => {
    const labelRef = useRef<HTMLSpanElement>(null);
    const [labelWidth, setLabelWidth] = useState(0);

    useLayoutEffect(() => {
        const node = labelRef.current;
        if (!node) return;
        // The text itself, plus the padding either side of it, read off the
        // element. `scrollWidth` was the obvious way and it under-reported by
        // an inset: the label is measured while its wrapper is collapsed to
        // nothing, and a squeezed box does not account for its own padding the
        // way a laid-out one does. A range around the text is unaffected by
        // the squeeze, and the padding is a number the element can be asked
        // for directly.
        const range = document.createRange();
        range.selectNodeContents(node);
        const { paddingLeft, paddingRight } = getComputedStyle(node);
        setLabelWidth(
            range.getBoundingClientRect().width +
                parseFloat(paddingLeft) +
                parseFloat(paddingRight),
        );
    }, [label]);

    return {
        labelRef,
        // Before the measurement lands there is nothing to open to, so the
        // control simply stays shut rather than opening to a guess.
        // The glyph and its trailing inset, plus the label — which already
        // carries the leading inset as its own padding. Adding the whole shut
        // size would count that inset twice and leave slack in front of the
        // word.
        labelledWidth: labelWidth
            ? `calc(${CONTROL_SIZE} - ${GLYPH_INSET} + ${labelWidth}px)`
            : CONTROL_SIZE,
    };
};
